import * as BABYLON from 'babylonjs';
import { WorldModel } from "../models/WorldModel";
import { Being } from "../models/Being";
import { Beast } from "../models/Beast";
import { landThresholds as thresholds, BRUSH_RADIUS, DirectionsVectors } from "../models/constants";
import { PubSub } from '../PubSub';
import type { GameEvents } from '../events';
import { DeadState } from '../controllers/states/being/DeadState';
import { HuntingState } from '../controllers/states/being/HuntingState';
import beingMeshUrl from '../assets/mesh/being.glb?url';

// Replace with proper mesh for beasts once we have one
const beastColours = {
    body: [139, 90, 43],     // Warm brown body
    projectile: [64, 52, 40] // Dark stone projectile particles
};

const groundColours = {
    sea: [110, 120, 130], // Slate seabed; the blue comes from the water surface above
    sand: [255, 217,  61], // Sandy yellow for beaches
    vegetation: [109, 171, 36], // Vegitation green
    highland: [175, 208, 77], // Dark green for high altitudes
    mountain: [137, 130, 102], // Mountain grey
    snow: [237, 230, 211], // Snow white
    dustParticle: [129, 77, 37] // Dust brown
};

export class WorldView {
    private scene: BABYLON.Scene;
    private ground!: BABYLON.Mesh;
    private water!: BABYLON.Mesh;
    private brushRing!: BABYLON.Mesh;
    private worldModel: WorldModel;
    private shadowGenerator: BABYLON.ShadowGenerator;
    private particleSystem!: BABYLON.ParticleSystem;
    private beingMeshes: Map<Being, BABYLON.AbstractMesh> = new Map();
    private beingAnimationGroups: Map<Being, BABYLON.AnimationGroup[]> = new Map();
    private meshToBeing: Map<BABYLON.AbstractMesh, Being> = new Map();
    private beingTemplate: BABYLON.AbstractMesh | null = null;
    private templateAnimationGroups: BABYLON.AnimationGroup[] = [];
    private hoveredBeing: Being | null = null;
    private hoverPanel!: HTMLDivElement;
    private gridIndices!: Uint32Array;
    private gridPositionsXZ!: Float32Array;
    private beastMeshes: Map<Beast, BABYLON.Mesh> = new Map();
    private beastMaterials: Map<Beast, BABYLON.StandardMaterial> = new Map();
    private huntingParticleSystems: Map<Being, BABYLON.ParticleSystem> = new Map();

    constructor(scene: BABYLON.Scene, worldModel: WorldModel, shadowGenerator: BABYLON.ShadowGenerator, pubsub: PubSub<GameEvents>) {
        this.scene = scene;
        this.worldModel = worldModel;
        this.shadowGenerator = shadowGenerator;
        this.createGround();
        this.createWater();
        this.createBrushRing();
        this.createParticleSystem();
        this.createHoverPanel();
        this.setupBeingHover();

        pubsub.subscribe('world:ground-updated', (center) => { this.updateGroundMesh(); this.spawnDust(center); });
        pubsub.subscribe('world:brush-update', ({ center, radius }) => this.updateBrushRing(center, radius));
        pubsub.subscribe('world:brush-hide', () => this.hideBrushRing());
        pubsub.subscribe('world:beings-update', ({ beings, instant }) => this.updateBeings(beings, instant));
        pubsub.subscribe('world:beings-spawned', (beings) => this.initializeBeings(beings));
        pubsub.subscribe('world:beasts-spawned', (beasts) => this.initializeBeasts(beasts));
        pubsub.subscribe('world:beasts-update', ({ beasts }) => this.updateBeasts(beasts));
        pubsub.subscribe('world:beast-killed', (position) => this.spawnKillEffect(position));
        pubsub.subscribe('world:being-gored', (position) => this.spawnKillEffect(position));
    }

    /** Returns the ground mesh so other systems can use it for raycasting. */
    public getGroundMesh(): BABYLON.Mesh {
        return this.ground;
    }

    /**
     * Loads the being GLB template (once) then clones it for each being in `beings`,
     * assigns a unique StandardMaterial per being, and registers each as a shadow caster.
     * The clone's animation groups are started looping immediately and left running for
     * the being's lifetime.
     * Each clone is positioned at its being's world position before being enabled, so
     * newly spawned beings (including beings born mid-game) appear in place rather than
     * flying in from the map centre.
     * Called whenever beings are spawned — at level start and on each birth.
     */
    private async initializeBeings(beings: Being[]): Promise<void> {
        if (!this.beingTemplate) {
            const result = await BABYLON.ImportMeshAsync(beingMeshUrl, this.scene);
            this.beingTemplate = result.meshes[0];
            this.beingTemplate.setEnabled(false);
            this.templateAnimationGroups = result.animationGroups;
            this.templateAnimationGroups.forEach(g => g.stop());
        }

        for (const being of beings) {
            const clone = this.beingTemplate.clone(`being_${Math.random()}`, null, false)!;
            clone.scaling.setAll(0.2);
            clone.rotationQuaternion = null;
            // Place the clone at the being's position before enabling it — a clone
            // starts at the origin, and the next beings-update would otherwise animate
            // it from the map centre to wherever the being actually is.
            clone.position.set(being.x, this.getAltitudeAt(being.x, being.z) + 0.1, being.z);
            clone.setEnabled(true);

            const allCloneNodes: BABYLON.Node[] = [clone, ...clone.getDescendants(false)];
            const nodeByName = new Map(allCloneNodes.map(n => [n.name, n]));
            const clonedGroups = this.templateAnimationGroups.map(g =>
                g.clone(g.name, (t: BABYLON.Node) => nodeByName.get(t.name) ?? t)
            );
            clonedGroups.forEach(g => g.start(true));
            this.beingAnimationGroups.set(being, clonedGroups);

            this.shadowGenerator.addShadowCaster(clone);
            this.beingMeshes.set(being, clone);
            this.meshToBeing.set(clone, being);
        }
    }

    /** Creates the floating HTML panel used to show a being's stats on hover. */
    private createHoverPanel(): void {
        this.hoverPanel = document.createElement('div');
        this.hoverPanel.className = 'being-hover-panel';
        this.hoverPanel.style.display = 'none';
        document.body.appendChild(this.hoverPanel);
    }

    /**
     * Hooks pointer move + per-frame projection so the hover panel tracks the
     * being under the cursor. Picking is restricted to being meshes so terrain
     * never triggers the panel.
     */
    private setupBeingHover(): void {
        this.scene.onPointerObservable.add((pointerInfo) => {
            if (pointerInfo.type !== BABYLON.PointerEventTypes.POINTERMOVE) return;
            const groundPick = this.scene.pick(
                this.scene.pointerX,
                this.scene.pointerY,
                (mesh) => mesh === this.ground
            );
            if (!groundPick?.hit || !groundPick.pickedPoint) {
                this.hoveredBeing = null;
                return;
            }
            this.hoveredBeing = this.findNearestBeingWithinRadius(groundPick.pickedPoint, BRUSH_RADIUS);
        });

        this.scene.onBeforeRenderObservable.add(() => this.updateHoverPanel());
    }

    /** Returns the closest living being to `point` within `radius` (XZ plane), or null. */
    private findNearestBeingWithinRadius(point: BABYLON.Vector3, radius: number): Being | null {
        let nearest: Being | null = null;
        let nearestDist = radius;
        for (const [being, mesh] of this.beingMeshes) {
            if (being.state === DeadState.stateName || !mesh.isEnabled()) continue;
            const dx = mesh.position.x - point.x;
            const dz = mesh.position.z - point.z;
            const dist = Math.sqrt(dx * dx + dz * dz);
            if (dist <= nearestDist) {
                nearest = being;
                nearestDist = dist;
            }
        }
        return nearest;
    }

    /** Repositions and refreshes the hover panel based on `hoveredBeing`. */
    private updateHoverPanel(): void {
        const being = this.hoveredBeing;
        if (!being || being.state === DeadState.stateName) {
            this.hoverPanel.style.display = 'none';
            return;
        }
        const mesh = this.beingMeshes.get(being);
        if (!mesh) { this.hoverPanel.style.display = 'none'; return; }

        const canvas = this.scene.getEngine().getRenderingCanvas()!;
        const rect = canvas.getBoundingClientRect();
        const screen = BABYLON.Vector3.Project(
            mesh.position,
            BABYLON.Matrix.Identity(),
            this.scene.getTransformMatrix(),
            this.camera!.viewport.toGlobal(rect.width, rect.height)
        );
        const healthPct = Math.max(0, Math.min(1, being.health)) * 100;
        this.hoverPanel.innerHTML =
            `<div class="being-hover-name">${being.name}</div>` +
            `<div class="being-hover-state">${being.state}</div>` +
            `<div class="being-hover-health"><div class="being-hover-health-fill" style="width:${healthPct}%"></div></div>`;
        this.hoverPanel.style.display = 'block';
        this.hoverPanel.style.left = `${rect.left + screen.x}px`;
        this.hoverPanel.style.top = `${rect.top + screen.y}px`;
    }

    private get camera(): BABYLON.Camera | null {
        return this.scene.activeCamera;
    }

    /**
     * Repositions and recolours each being's mesh to reflect its current state.
     * Dead beings are hidden. Position updates are instant when `instant` is true,
     * otherwise animated over 30 frames. Skeletal animation is not touched here — it
     * runs continuously from spawn (see `initializeBeings`).
     */
    private updateBeings(beings: Being[], instant = false): void {
        this.updateHuntingParticles(beings);
        for (const being of beings) {
            const mesh = this.beingMeshes.get(being);
            if (!mesh) continue;

            if (being.state === DeadState.stateName) {
                mesh.setEnabled(false);
                continue;
            }

            const altitude = this.getAltitudeAt(being.x, being.z);
            const targetPosition = new BABYLON.Vector3(being.x, altitude + 0.1, being.z);
            if (mesh.position.subtract(targetPosition).length() > 0) {
                if (instant) {
                    this.scene.stopAnimation(mesh);
                    mesh.position.set(targetPosition.x, targetPosition.y, targetPosition.z);
                } else {
                    BABYLON.Animation.CreateAndStartAnimation(
                        'beingMove', mesh, 'position',
                        60, 30,
                        mesh.position.clone(), targetPosition,
                        BABYLON.Animation.ANIMATIONLOOPMODE_CONSTANT
                    );
                }
            }

            if (being.moveDirection !== null) {
                const dir = DirectionsVectors.get(being.moveDirection);
                if (dir) {
                    const targetRotationY = Math.atan2(dir.dx, dir.dz) + Math.PI / -2;
                    if (instant) {
                        mesh.rotation.y = targetRotationY;
                    } else {
                        BABYLON.Animation.CreateAndStartAnimation(
                            'beingRotate', mesh, 'rotation.y',
                            60, 30,
                            mesh.rotation.y, targetRotationY,
                            BABYLON.Animation.ANIMATIONLOOPMODE_CONSTANT
                        );
                    }
                }
            }
        }
    }

    /**
     * Returns the terrain height at the grid cell nearest to (`worldX`, `worldZ`).
     * Returns 0 for out-of-bounds positions.
     */
    private getAltitudeAt(worldX: number, worldZ: number): number {
        const terrainSize = this.worldModel.terrainSize;
        const subdivisions = this.worldModel.subdivisions;
        const terrainHeights = this.worldModel.terrainHeights;

        const col = Math.round(((worldX + terrainSize / 2) / terrainSize) * subdivisions);
        const row = Math.round(((terrainSize / 2 - worldZ) / terrainSize) * subdivisions);
        const gridIndex = row * (subdivisions + 1) + col;

        return terrainHeights[gridIndex] || 0;
    }

    /**
     * Creates the dust particle system used for terrain-edit feedback.
     * The system starts immediately but emits no particles until `spawnDust` is called.
     */
    private createParticleSystem(): void {
        this.particleSystem = new BABYLON.ParticleSystem("particles", 200, this.scene);

        this.particleSystem.particleTexture = new BABYLON.Texture("/dust.svg", this.scene);

        this.particleSystem.emitter = new BABYLON.Vector3(0, 0, 0);
        this.particleSystem.minEmitBox = new BABYLON.Vector3(-5, 0, -5);
        this.particleSystem.maxEmitBox = new BABYLON.Vector3(5, 0, 5);

        const dustColor = WorldView.rgbToColor3(groundColours.dustParticle);
        this.particleSystem.color1 = new BABYLON.Color4(dustColor[0], dustColor[1], dustColor[2], 1.0);
        this.particleSystem.color2 = new BABYLON.Color4(dustColor[0] * 0.8, dustColor[1] * 0.8, dustColor[2] * 0.8, 1.0);
        this.particleSystem.colorDead = new BABYLON.Color4(dustColor[0] * 0.5, dustColor[1] * 0.5, dustColor[2] * 0.5, 0.0);

        this.particleSystem.minSize = 0.1;
        this.particleSystem.maxSize = 0.5;

        this.particleSystem.minLifeTime = 0.3;
        this.particleSystem.maxLifeTime = 1.0;

        this.particleSystem.emitRate = 0; // Manual burst only
        this.particleSystem.blendMode = BABYLON.ParticleSystem.BLENDMODE_STANDARD;

        this.particleSystem.gravity = new BABYLON.Vector3(0, -9.81, 0);
        this.particleSystem.direction1 = new BABYLON.Vector3(-4, 4, 4);
        this.particleSystem.direction2 = new BABYLON.Vector3(4, 4, -4);

        this.particleSystem.minAngularSpeed = 0;
        this.particleSystem.maxAngularSpeed = Math.PI;

        this.particleSystem.minEmitPower = 1;
        this.particleSystem.maxEmitPower = 3;
        this.particleSystem.updateSpeed = 0.005;

        this.particleSystem.start();
    }

    /** Emits a burst of 20 dust particles centred 1 unit above `position`. */
    private spawnDust(position: BABYLON.Vector3): void {
        this.particleSystem.emitter = position.add(new BABYLON.Vector3(0, 1.0, 0));
        this.particleSystem.manualEmitCount = 20;
    }

    /** Creates the gold torus brush-ring indicator mesh (initially hidden). */
    private createBrushRing(): void {
        this.brushRing = BABYLON.MeshBuilder.CreateTorus("brushRing", {
            diameter: 1,
            thickness: 0.1,
            tessellation: 32
        }, this.scene);

        const ringMat = new BABYLON.StandardMaterial("ringMat", this.scene);
        ringMat.diffuseColor = new BABYLON.Color3(1.0, 0.84, 0.0);
        ringMat.emissiveColor = new BABYLON.Color3(0.8, 0.67, 0.0);
        ringMat.alpha = 0.25;
        this.brushRing.material = ringMat;
        this.brushRing.isPickable = false;
        this.brushRing.isVisible = false;
    }

    /**
     * Moves and scales the brush ring to `position` at the given `radius`,
     * makes it visible, and hides the OS cursor.
     */
    private updateBrushRing(position: BABYLON.Vector3, radius: number): void {
        this.brushRing.position = position.add(new BABYLON.Vector3(0, 2, 0)); // Slightly above ground to prevent z-fighting
        this.brushRing.scaling = new BABYLON.Vector3(radius, 1, radius);
        this.brushRing.isVisible = true;
        this.scene.getEngine().getRenderingCanvas()!.style.cursor = 'none';
    }

    /** Hides the brush ring and restores the OS cursor. */
    private hideBrushRing(): void {
        this.brushRing.isVisible = false;
        this.scene.getEngine().getRenderingCanvas()!.style.cursor = '';
    }

    /** Converts an RGB array with values in [0, 255] to [0, 1]. */
    private static rgbToColor3(rgb: number[]): number[] {
        return rgb.map(c => c / 255);
    }

    /**
     * Creates the ground mesh and material, then runs an initial `updateGroundMesh`
     * to populate vertex positions and colours.
     */
    private createGround(): void {
        this.ground = BABYLON.MeshBuilder.CreateGround("ground", {
            width: this.worldModel.terrainSize,
            height: this.worldModel.terrainSize,
            subdivisions: this.worldModel.subdivisions,
            updatable: true
        }, this.scene);

        // Cache the original indexed grid layout, then re-author the mesh as
        // non-indexed (one unique vertex per triangle corner). Shared vertices
        // would cause ComputeNormals to average face normals into a smooth
        // surface; flat shading requires each face to own its three normals.
        const indexedPositions = this.ground.getVerticesData(BABYLON.VertexBuffer.PositionKind)!;
        const indexedIndices = this.ground.getIndices()!;

        const numGridVerts = indexedPositions.length / 3;
        this.gridPositionsXZ = new Float32Array(numGridVerts * 2);
        for (let i = 0; i < numGridVerts; i++) {
            this.gridPositionsXZ[i * 2]     = indexedPositions[i * 3];
            this.gridPositionsXZ[i * 2 + 1] = indexedPositions[i * 3 + 2];
        }
        this.gridIndices = new Uint32Array(indexedIndices);

        const numFaceVerts = this.gridIndices.length;
        const sequentialIndices = new Uint32Array(numFaceVerts);
        for (let i = 0; i < numFaceVerts; i++) sequentialIndices[i] = i;
        this.ground.setVerticesData(BABYLON.VertexBuffer.PositionKind, new Float32Array(numFaceVerts * 3), true);
        this.ground.setVerticesData(BABYLON.VertexBuffer.NormalKind, new Float32Array(numFaceVerts * 3), true);
        this.ground.setVerticesData(BABYLON.VertexBuffer.ColorKind, new Float32Array(numFaceVerts * 4), true);
        this.ground.setIndices(sequentialIndices, numFaceVerts);

        const groundMat = new BABYLON.StandardMaterial("groundMat", this.scene);
        groundMat.diffuseColor = new BABYLON.Color3(1, 1, 1);
        groundMat.specularColor = new BABYLON.Color3(0, 0, 0);
        groundMat.backFaceCulling = false;
        this.ground.material = groundMat;
        this.ground.receiveShadows = true;

        this.updateGroundMesh();
    }

    /**
     * Synchronises the Babylon ground mesh with the current terrain height data.
     * Rebuilds the non-indexed position/colour buffers from `terrainHeights`
     * (one vertex per triangle corner, coloured by the triangle's average
     * altitude) and recomputes normals — yielding flat / faceted shading.
     */
    private updateGroundMesh(): void {
        const heights = this.worldModel.terrainHeights;
        const indices = this.gridIndices;
        const xz = this.gridPositionsXZ;

        const numFaceVerts = indices.length;
        const positions = new Float32Array(numFaceVerts * 3);
        const colors = new Float32Array(numFaceVerts * 4);

        for (let t = 0; t < numFaceVerts; t += 3) {
            const i0 = indices[t], i1 = indices[t + 1], i2 = indices[t + 2];
            const h0 = heights[i0], h1 = heights[i1], h2 = heights[i2];
            const color = WorldView.rgbToColor3(WorldView.colorForHeight((h0 + h1 + h2) / 3));

            const corners = [i0, i1, i2];
            const cornerHeights = [h0, h1, h2];
            for (let k = 0; k < 3; k++) {
                const gridIdx = corners[k];
                const base = (t + k) * 3;
                positions[base]     = xz[gridIdx * 2];
                positions[base + 1] = cornerHeights[k];
                positions[base + 2] = xz[gridIdx * 2 + 1];

                const cBase = (t + k) * 4;
                colors[cBase]     = color[0];
                colors[cBase + 1] = color[1];
                colors[cBase + 2] = color[2];
                colors[cBase + 3] = 1;
            }
        }

        this.ground.updateVerticesData(BABYLON.VertexBuffer.PositionKind, positions);
        this.ground.updateVerticesData(BABYLON.VertexBuffer.ColorKind, colors);

        const normals: number[] = [];
        BABYLON.VertexData.ComputeNormals(positions, this.ground.getIndices()!, normals);
        this.ground.updateVerticesData(BABYLON.VertexBuffer.NormalKind, normals);

        this.ground.refreshBoundingInfo();
    }

    /**
     * Creates a flat water plane at sea level as a slightly translucent steel-blue
     * layer. The plane spans the full terrain; its low alpha lets the submerged
     * terrain show through while the blue tint reads clearly as water sitting on
     * top of it.
     */
    private createWater(): void {
        const size = this.worldModel.terrainSize;
        this.water = BABYLON.MeshBuilder.CreateGround("water", {
            width: size,
            height: size,
            subdivisions: this.worldModel.subdivisions, // Match the ground's resolution
            updatable: true // Vertices are oscillated each frame in animateWater()
        }, this.scene);
        // Sit a hair below sea level so flat terrain that rests exactly at
        // `thresholds.sea` still reads as land (no z-fighting); genuine basins
        // below sea level fill with water.
        this.water.position.y = thresholds.sea - 0.3;
        this.water.isPickable = false; // Never intercept terrain brush raycasts

        const waterMat = new BABYLON.StandardMaterial("waterMat", this.scene);
        const [r, g, b] = WorldView.rgbToColor3([45, 110, 190]); // Blue water surface
        waterMat.diffuseColor = new BABYLON.Color3(r, g, b);
        waterMat.specularColor = new BABYLON.Color3(0.2, 0.2, 0.2);
        // Translucent so submerged terrain reads through the surface.
        waterMat.alpha = 0.5;
        this.water.material = waterMat;

        this.animateWater();
    }

    /**
     * Oscillates the water mesh's vertices up and down each frame. Every vertex
     * follows the same sine wave over time but is given a fixed random phase
     * offset generated once at startup, so neighbouring points move out of step
     * with each other — a choppy surface rather than a smooth rolling swell.
     *
     * The mesh is first re-authored as non-indexed geometry (one vertex per
     * triangle corner) so `ComputeNormals` produces flat / faceted shading that
     * matches the low-poly ground. The phase offset is keyed to the *grid*
     * vertex, so the duplicated corners sharing a grid point use the same offset
     * and stay coincident — the surface gets faceted lighting without tearing.
     */
    private animateWater(): void {
        const indexedPositions = this.water.getVerticesData(BABYLON.VertexBuffer.PositionKind)!;
        const indexedIndices = this.water.getIndices()!;

        const numGridVerts = indexedPositions.length / 3;
        const gridXZ = new Float32Array(numGridVerts * 2);
        for (let i = 0; i < numGridVerts; i++) {
            gridXZ[i * 2]     = indexedPositions[i * 3];
            gridXZ[i * 2 + 1] = indexedPositions[i * 3 + 2];
        }
        const gridIndices = new Uint32Array(indexedIndices);

        const numFaceVerts = gridIndices.length;
        const positions = new Float32Array(numFaceVerts * 3);
        for (let t = 0; t < numFaceVerts; t++) {
            const gridIdx = gridIndices[t];
            positions[t * 3]     = gridXZ[gridIdx * 2];     // X is static
            positions[t * 3 + 2] = gridXZ[gridIdx * 2 + 1]; // Z is static; Y animates
        }
        const sequentialIndices = new Uint32Array(numFaceVerts);
        for (let i = 0; i < numFaceVerts; i++) sequentialIndices[i] = i;
        this.water.setVerticesData(BABYLON.VertexBuffer.PositionKind, positions, true);
        this.water.setVerticesData(BABYLON.VertexBuffer.NormalKind, new Float32Array(numFaceVerts * 3), true);
        this.water.setIndices(sequentialIndices, numFaceVerts);

        // One fixed random phase offset per *grid* vertex, so each point
        // oscillates independently while shared corners stay coincident.
        const phaseOffsets = new Float32Array(numGridVerts);
        for (let v = 0; v < numGridVerts; v++) {
            phaseOffsets[v] = Math.random() * Math.PI * 2;
        }

        const normals: number[] = [];
        const AMP = 0.2;      // Wave height
        const SPEED = 0.0015; // Elapsed ms → wave phase

        this.scene.onBeforeRenderObservable.add(() => {
            const t = performance.now() * SPEED;
            for (let f = 0; f < numFaceVerts; f++) {
                // If the ground is above sea level, set the water vertex to the ground height to
                // hide it beneath the terrain; otherwise, oscillate the water vertex up and down
                // around sea level
                const groundHeight = this.worldModel.terrainHeights[gridIndices[f]];
                if (groundHeight >= thresholds.sea) {
                    positions[f * 3 + 1] = groundHeight;
                } else {
                    positions[f * 3 + 1] = Math.sin(t + phaseOffsets[gridIndices[f]]) * AMP;
                }
            }
            this.water.updateVerticesData(BABYLON.VertexBuffer.PositionKind, positions);
            BABYLON.VertexData.ComputeNormals(positions, sequentialIndices, normals);
            this.water.updateVerticesData(BABYLON.VertexBuffer.NormalKind, normals);
        });
    }

    /**
     * Maps a terrain height value to a flat-shading colour.
     * Thresholds match `landThresholds` in constants.ts.
     * The sand band sits just below sea level so shallow water reads as beach;
     * flat terrain at sea level (height 0) renders as vegetation, not sand.
     */
    private static colorForHeight(height: number): number[] {
        if (height > thresholds.snow)                        return groundColours.snow;
        if (height > thresholds.mountain)                    return groundColours.mountain;
        if (height > thresholds.highland)                    return groundColours.highland;
        if (height >= thresholds.sea)                        return groundColours.vegetation;
        if (height >= thresholds.sea - thresholds.sandBand)  return groundColours.sand;
        return groundColours.sea;
    }

    /**
     * Creates a box mesh for each beast and registers it as a shadow caster.
     * Called once when beasts are first spawned.
     */
    private initializeBeasts(beasts: Beast[]): void {
        for (const beast of beasts) {
            const mesh = BABYLON.MeshBuilder.CreateBox(`beast_${Math.random()}`, { width: 2, height: 1.2, depth: 2 }, this.scene);
            mesh.scaling.setAll(1);
            // Position at the beast's real location immediately. CreateBox defaults
            // to the origin, so without this the mesh sits at map centre until the
            // next world:beasts-update tick (up to one 500ms sim tick away).
            const altitude = this.getAltitudeAt(beast.x, beast.z);
            mesh.position.set(beast.x, altitude + 0.6, beast.z);
            const mat = new BABYLON.StandardMaterial(`beastMat_${Math.random()}`, this.scene);
            const [r, g, b] = WorldView.rgbToColor3(beastColours.body);
            mat.diffuseColor = new BABYLON.Color3(r, g, b);
            mesh.material = mat;
            this.shadowGenerator.addShadowCaster(mesh);
            this.beastMeshes.set(beast, mesh);
            this.beastMaterials.set(beast, mat);
        }
    }

    /**
     * Repositions each beast mesh to track its model position. Beasts no longer in
     * the world model (killed) have their mesh and material disposed.
     */
    private updateBeasts(beasts: Beast[]): void {
        // Remove meshes for beasts that are no longer in the world model
        for (const [beast, mesh] of this.beastMeshes) {
            if (!beasts.includes(beast)) {
                mesh.dispose();
                this.beastMaterials.get(beast)?.dispose();
                this.beastMeshes.delete(beast);
                this.beastMaterials.delete(beast);
            }
        }

        for (const beast of beasts) {
            const mesh = this.beastMeshes.get(beast);
            if (!mesh) continue;

            const altitude = this.getAltitudeAt(beast.x, beast.z);
            mesh.position.set(beast.x, altitude + 0.6, beast.z);
        }
    }

    /** Emits a burst of dust particles at `position` to mark a kill — either a beast
     *  brought down by the pack, or a being gored by an angered beast. */
    private spawnKillEffect(position: { x: number; z: number }): void {
        // Reuse the existing dust particle system, repositioning its emitter to the
        // death location first — otherwise the burst fires wherever the last terrain
        // edit left the emitter (the map centre on a fresh game).
        const altitude = this.getAltitudeAt(position.x, position.z);
        this.particleSystem.emitter = new BABYLON.Vector3(position.x, altitude + 1.0, position.z);
        this.particleSystem.manualEmitCount = 60;
    }

    /**
     * Creates or destroys hunting projectile particle systems as beings enter and
     * leave the Hunting state. Active systems are repositioned each frame to track
     * the hunting being's current position.
     *
     * Only beings that have closed to within `HuntingState.HUNT_RANGE_SQ` of a live
     * beast throw, matching where damage actually lands — a hunter still charging
     * deals nothing, so showing its projectiles would misreport who is contributing
     * to the kill.
     */
    private updateHuntingParticles(beings: Being[]): void {
        const hunters = new Set(
            beings.filter(b => b.state === HuntingState.stateName && this.isInThrowingRange(b))
        );

        for (const being of hunters) {
            const mesh = this.beingMeshes.get(being);
            if (!mesh) continue;

            let ps = this.huntingParticleSystems.get(being);
            if (!ps) {
                ps = this.createHuntingParticleSystem(being);
                this.huntingParticleSystems.set(being, ps);
            }

            // Track the hunter every tick. The emitter is a world-space Vector3 rather
            // than the being mesh: the mesh is scaled to 0.2, and a mesh emitter
            // transforms both the emit box and the particle velocities by that scale,
            // shrinking the projectiles to a near-invisible speck — which is why they
            // couldn't be seen. A Vector3 emitter keeps everything in world units.
            (ps.emitter as BABYLON.Vector3).copyFrom(mesh.position);
            (ps.emitter as BABYLON.Vector3).y += 0.3;

            this.aimHuntingParticles(ps, being);
        }

        // Dispose systems for beings that stopped hunting, or fell out of range
        for (const [being, ps] of this.huntingParticleSystems) {
            if (!hunters.has(being)) {
                ps.dispose();
                this.huntingParticleSystems.delete(being);
            }
        }
    }

    /** True when a live beast is within the being's throwing range. */
    private isInThrowingRange(being: Being): boolean {
        return this.worldModel.beasts.some(beast => {
            if (beast.health <= 0) return false;
            const dx = beast.x - being.x;
            const dz = beast.z - being.z;
            return dx * dx + dz * dz <= HuntingState.HUNT_RANGE_SQ;
        });
    }

    /**
     * Builds a dark-stone projectile particle system for a hunting being, aimed at
     * the nearest beast. The emitter is a world-space Vector3 that the caller
     * repositions each tick to follow the being.
     */
    private createHuntingParticleSystem(being: Being): BABYLON.ParticleSystem {
        const ps = new BABYLON.ParticleSystem(`hunt_${Math.random()}`, 30, this.scene);
        ps.particleTexture = new BABYLON.Texture('/dust.svg', this.scene);
        ps.emitter = new BABYLON.Vector3(being.x, 0, being.z);
        ps.minEmitBox = new BABYLON.Vector3(-0.2, 0, -0.2);
        ps.maxEmitBox = new BABYLON.Vector3(0.2, 0, 0.2);
        const [r, g, b] = WorldView.rgbToColor3(beastColours.projectile);
        ps.color1 = new BABYLON.Color4(r, g, b, 1.0);
        ps.color2 = new BABYLON.Color4(r * 0.8, g * 0.8, b * 0.8, 1.0);
        // Stay fully opaque almost to the end, then snap out — keeps each particle
        // reading as a solid stone rather than a translucent puff.
        ps.colorDead = new BABYLON.Color4(r * 0.6, g * 0.6, b * 0.6, 0.6);
        ps.minSize = 0.3;
        ps.maxSize = 0.5;
        ps.minLifeTime = 0.35;
        ps.maxLifeTime = 0.55;
        ps.emitRate = 10;
        ps.blendMode = BABYLON.ParticleSystem.BLENDMODE_STANDARD;
        ps.gravity = new BABYLON.Vector3(0, -8, 0);

        this.aimHuntingParticles(ps, being);
        ps.minEmitPower = 3;
        ps.maxEmitPower = 6;
        ps.updateSpeed = 0.01;
        ps.start();
        return ps;
    }

    /**
     * Points a hunting particle system's emission cone at the being's nearest beast.
     * Called every tick as well as on creation: the target is usually fleeing, so an
     * aim fixed at creation time would leave the arc trailing behind the beast.
     */
    private aimHuntingParticles(ps: BABYLON.ParticleSystem, being: Being): void {
        const nearestBeast = this.findNearestBeast(being);
        if (!nearestBeast) {
            ps.direction1 = new BABYLON.Vector3(-3, 3, -3);
            ps.direction2 = new BABYLON.Vector3(3, 5, 3);
            return;
        }

        const dx = nearestBeast.x - being.x;
        const dz = nearestBeast.z - being.z;
        const len = Math.sqrt(dx * dx + dz * dz) || 1;
        ps.direction1 = new BABYLON.Vector3((dx / len) * 4, 1, (dz / len) * 4);
        ps.direction2 = new BABYLON.Vector3((dx / len) * 6, 2, (dz / len) * 6);
    }

    /** Returns the nearest beast to a being from the world model, or null if none. */
    private findNearestBeast(being: Being): Beast | null {
        let nearest: Beast | null = null;
        let nearestDistSq = Infinity;
        for (const beast of this.worldModel.beasts) {
            const dx = beast.x - being.x;
            const dz = beast.z - being.z;
            const distSq = dx * dx + dz * dz;
            if (distSq < nearestDistSq) {
                nearestDistSq = distSq;
                nearest = beast;
            }
        }
        return nearest;
    }
}
